// Underlyings by their canonical names, every listing name they trade under, and their asset class.
// Ports `ALIASES`/`canonical` of atlas-geo `exposure.rs` and the class tables of `class.rs`, with
// the names Hyperliquid's dexes list today (main dex and the HIP-3 dexes: xyz, km, mkts, para, io,
// cash, flx, vntl, hyna).
//
// One change from atlas: the canonical name is the market code, not the plain word (`XAU`, not
// `GOLD`; `BZ`, not `BRENTOIL`), so it agrees with the underlyings the headline families name
// (`headlines.tsv` already writes `XAU`, `XAG`, `XCU`, `XPT`, `XPD`, `CL`, `BZ`). No venue is in a
// canonical name.

import type { AssetClass } from "./types.ts";

/** Names one underlying goes by, canonical first. Matched case aside. */
const GROUPS: readonly (readonly string[])[] = [
  // metals
  ["XAU", "GOLD", "PAXG", "XAUT", "XAUUSD", "GC"],
  ["XAG", "SILVER", "XAGUSD"],
  ["XCU", "COPPER", "HG"],
  ["XPT", "PLATINUM", "PLAT"],
  ["XPD", "PALLADIUM", "PALL"],
  ["ALUMINIUM", "ALUMINUM", "ALUM"],
  // energy: WTI is CL, Brent is BZ (atlas: CL and BRENTOIL; xyz lists CL and BRENTOIL)
  ["CL", "WTI", "USOIL", "OIL", "WTIOIL", "CRUDE"],
  ["BZ", "BRENTOIL", "BRENT", "UKOIL"],
  ["NATGAS", "NG", "NGAS"],
  ["HO", "HEATINGOIL"],
  ["SOY", "SOYBEAN", "SOYBEANS"],
  // indices. Not SPX: on Hyperliquid's main dex that is the SPX6900 memecoin.
  ["SP500", "US500", "USA500", "SPY"],
  // xyz's XYZ100 tracks the Nasdaq-100 (atlas: "NDX, not XYZ100")
  ["NDX", "XYZ100", "US100", "USA100", "USTECH", "NAS100", "QQQ"],
  ["DJI", "US30", "USA30", "DOW", "DJIA", "DIA"],
  ["RUT", "US2000", "SMALL2000", "RUSSELL", "RUSSELL2000", "IWM"],
  ["N225", "JP225", "JPN225", "NIKKEI", "NIKKEI225"],
  ["KOSPI", "KR200", "KOSPI200", "KRCOMP"],
  ["HSI", "HK50", "HANGSENG"],
  ["DAX", "DE40", "GER40"],
  ["FTSE", "UK100"],
  ["ESTX50", "STOXX50", "EU50", "SX5E"],
  ["CAC", "CAC40", "FR40"],
  ["IBOV", "IBOVESPA", "BOVESPA"],
  ["NIFTY", "NIFTY50"],
  // FX: the currency, against the dollar
  ["EUR", "EURUSD"],
  ["JPY", "USDJPY", "JPYUSD"],
  ["GBP", "GBPUSD"],
  ["CNH", "CNY", "USDCNH", "USDCNY", "RMB"],
  ["CHF", "USDCHF"],
  ["AUD", "AUDUSD"],
  ["CAD", "USDCAD"],
  ["NZD", "NZDUSD"],
  ["KRW", "USDKRW"],
  ["HKD", "USDHKD"],
  ["DXY", "USDX"],
  // rates
  ["US10Y", "10Y", "TNX", "UST10Y"],
  ["US2Y", "2Y"],
  ["US30Y", "30Y"],
  // companies listed under more than one name
  ["GOOGL", "GOOG"],
  ["SAMSUNG", "SMSN", "SAMSUNGUSD"],
  ["SKHYNIX", "SKHX", "SKHY", "SKHYNIXUSD"],
  ["HYUNDAI", "HYUNDAIUSD"],
  ["TENCENT", "TCNT", "TCEHY"],
  ["SPCX", "SPACEX"],
  ["OPENAI", "OAI"],
  ["ANTHROPIC", "ANTH", "ANTHRO"],
  // crypto
  ["BTC", "XBT"],
  ["POL", "MATIC"],
  ["RENDER", "RNDR"],
  ["SPX", "SPX6900"],
  ["TON", "TONCOIN"],
];

/** Coins Hyperliquid lists per thousand: `kPEPE` (main dex) and `1000PEPE` (elsewhere) are PEPE. */
const PER_THOUSAND = ["PEPE", "SHIB", "BONK", "FLOKI", "LUNC", "NEIRO", "DOGS", "SATS"];

/** Names that mean something else on the main dex: read as these only behind a HIP-3 dex prefix
 * (`flx:GAS` was natural gas; main-dex `GAS` is a token). */
const DEX_ONLY: Readonly<Record<string, string>> = { GAS: "NATGAS", HH: "NATGAS" };

/** Asset classes, earliest set first where a name is in two. Space-separated, canonical names and
 * listing names alike. Ported from atlas `class.rs` (`KEYWORDS`, `LISTED`, `FX`), its six classes
 * folded into ours: private stocks are equities; the alternatives split into fx, rates, and baskets
 * (indices); H100 (GPU rental) is filed as a commodity. */
const CLASS_TABLE: readonly (readonly [AssetClass, string])[] = [
  ["rates", "US10Y US2Y US30Y 10Y 2Y 30Y TNX UST10Y USBOND TLT SGOV"],
  [
    "fx",
    "EUR GBP JPY CHF AUD CAD NZD KRW CNH CNY HKD DXY USDX EURUSD GBPUSD USDJPY JPYUSD USDCHF AUDUSD USDCAD " +
      "NZDUSD USDKRW USDCNH USDCNY USDHKD RMB",
  ],
  [
    "indices",
    // indices
    "SP500 NDX DJI RUT N225 KOSPI HSI DAX FTSE ESTX50 CAC IBOV NIFTY VIX US500 USA500 US100 USA100 US30 USA30 " +
      "NAS100 DOW DJIA RUSSELL US2000 SMALL2000 JP225 JPN225 NIKKEI KR200 KRCOMP USTECH XYZ100 ASX SMI IBEX STOXX " +
      // funds that track an index, a country or a sector
      "SPY QQQ DIA IWM EWY EWJ EWT EWZ SOXX SOXL SOXS SMH XBI XLE IGV KWEB GDX TQQQ UVXY " +
      // baskets
      "MAG7 FANG SEMIS SEMI ENERGYUSD INFOTE INFOTECH DEFENSE ROBOTHOUSE ROBOT NUCLEA NUCLEAR AIBASK UNICORN ENERGY " +
      "BIOTECH USENERGY GLDMINE GOLDJM SILVERJM MAGS BOTZ KORU DRAM",
  ],
  [
    "commodities",
    "XAU XAG XCU XPT XPD CL BZ NATGAS HO RB TTF NBP JKM URANIUM ALUMINIUM ZINC NICKEL LEAD TIN IRON COAL EUA CCL " +
      "NEWC DUBAI URALS WHEAT CORN SOY COFFEE SUGAR COCOA COTTON RICE OATS CATTLE HOGS LUMBER OJ H100 SLV USO URA URNM",
  ],
  [
    "equities",
    // private companies
    "SPCX OPENAI ANTHROPIC STRIPE DATABRICKS CANVA DISCORD KRAKEN XAI PERPLEXITY EPIC ROBLX SHEIN CXMT MINIMAX " +
      "ZHIPU UNITREE OURA CBRS MOONSHOT " +
      // listed on Hyperliquid's dexes (atlas `LISTED`, and xyz, para, io, km, cash today)
      "AAOI AAPL ADBE ADI AGPU AMAT AMC AMD AMZN ARM ASML ASTS AVGO AXTI BABA BBX BE BMNR BNC BRKB BX BYD CLSK " +
      "COHR COIN COST CRCL CRDO CRM CRWD CRWV CSCO DELL DIS DKNG EBAY FLNC FUTU GEV GLW GME GOOGL GPRO HANMI HD HIMS " +
      "HOOD HPE HYUNDAI IBM INTC IREN JPM KIOXIA KSTR KUAISHOU LITE LLY LUNR LYTE MEITUAN META MRNA MRVL MSFT MSTR MU " +
      "MUU NBIS NFLX NOK NOW NVDA NVO ONDS ORCL PAYP PLTR POPMART QBTS QCOM QNTX RGTI RIVN RKLB SAMSUNG SKHYNIX " +
      "SMCI SMIC SNDK SNXX SOFI STRC STXX TENCENT TSLA TSM TTWO UBER USAR V WDC WMT WULF XIAOMI ZM SOFTBANK BB NET " +
      "CVX LRCX UMC TWST RDDT PURRDAT BIRD QNT BOT SHAZ NCLD GIGADEV IBIDEN ACN INNOLIGHT AMEC CATL RELIANCE JIO " +
      "CAMBRICON YMTC VST TER CIEN MELI CIFR IONQ RTX CAR PDD NAVER",
  ],
  [
    "crypto",
    // gauges of the crypto market itself
    "BTCD TOTAL2 TOTAL3 OTHERS USDTD BVIV " +
      // coins the newsroom names (no ticker that is a stock on a HIP-3 dex: STX is Seagate on para)
      "BTC ETH SOL XRP DOGE HYPE BNB ADA LINK AVAX SUI TRX TON LTC DOT PEPE SHIB ENA AAVE UNI NEAR APT ARB OP TIA " +
      "WLD FARTCOIN PUMP TRUMP MELANIA WLFI BCH ETC XMR ZEC BSV TAO WIF BONK FLOKI LUNC NEIRO DOGS SATS INJ ONDO " +
      "SEI JUP LDO PENDLE FIL ICP ATOM XLM HBAR KAS ALGO BERA PENGU ASTER XPL MON ZRO EIGEN RENDER FET VIRTUAL " +
      "POPCAT MOODENG SPX PURR LIT LIGHTER IP USDE CRV GMX DYDX SNX COMP MORPHO KAITO ZORA LINEA MEGA POL",
  ],
];

/** Parts of a name that make it a commodity (`HEATINGOIL`, `GOLDJM`), `OIL` as a whole word
 * (`COMMODITY_PARTS` in atlas `class.rs`): the last resort for a name no table knows. */
const COMMODITY_PARTS = [
  "CRUDE", "BRENT", "WTI", "NATGAS", "GASOIL", "GASOLINE", "HEATING", "COAL", "CARBON", "URANIUM", "PALLADIUM",
  "PLATINUM", "SILVER", "GOLD", "COPPER", "ALUMIN", "ZINC", "NICKEL", "WHEAT", "CORN", "SOY", "COFFEE", "SUGAR",
  "COCOA", "COTTON", "LUMBER", "CATTLE",
];

/* ── indexes, built once ─────────────────────────────────────────── */

/** Upper-case name to canonical. */
const CANON = new Map<string, string>();
/** Canonical to every name, canonical first, as Hyperliquid writes them. */
const NAMES = new Map<string, string[]>();
const CLASS = new Map<string, AssetClass>();

function addAlias(canon: string, name: string): void {
  const key = name.toUpperCase();
  if (!CANON.has(key)) CANON.set(key, canon);
  const list = NAMES.get(canon) ?? [canon];
  if (!list.includes(name)) list.push(name);
  NAMES.set(canon, list);
}

for (const group of GROUPS) {
  const canon = group[0];
  if (canon === undefined) continue;
  for (const name of group) addAlias(canon, name);
}
for (const coin of PER_THOUSAND) {
  addAlias(coin, coin);
  addAlias(coin, `k${coin}`);
  addAlias(coin, `1000${coin}`);
}
for (const [cls, names] of CLASS_TABLE) {
  for (const name of names.split(/\s+/)) {
    if (name && !CLASS.has(name)) CLASS.set(name, cls);
  }
}

/* ── the contract ─────────────────────────────────────────────────── */

const QUOTES = ["USDC", "USDT", "USDG", "USDE", "USD"];

/** The symbol without its HIP-3 dex (`xyz:GOLD` → `GOLD`), and whether it had one. */
function undex(sym: string): { bare: string; dexed: boolean } {
  const s = sym.trim();
  const colon = s.indexOf(":");
  return colon >= 0 ? { bare: s.slice(colon + 1), dexed: true } : { bare: s, dexed: false };
}

function known(upper: string): boolean {
  return CANON.has(upper) || CLASS.has(upper);
}

/** The canonical name of an underlying, from any of its names or listings, case aside: `xyz:GOLD`,
 * `PAXG` and `gold` are `XAU`; `xyz:XYZ100` is `NDX`; `kPEPE` and `1000PEPE` are `PEPE`; a quoted
 * pair of a known base (`BTCUSDT`) is its base. Anything else comes back upper-case, without its
 * dex. */
export function canonical(sym: string): string {
  const { bare, dexed } = undex(sym);
  const upper = bare.toUpperCase();
  if (dexed) {
    const only = DEX_ONLY[upper];
    if (only !== undefined) return only;
  }
  const hit = CANON.get(upper);
  if (hit !== undefined) return hit;
  // Hyperliquid's per-thousand listings: a lower-case k before capitals, or 1000
  if (/^k[A-Z][A-Z0-9]+$/.test(bare)) return canonical(bare.slice(1));
  if (/^1000[A-Z][A-Z0-9]*$/.test(upper)) return canonical(upper.slice(4));
  for (const quote of QUOTES) {
    if (upper.length > quote.length + 1 && upper.endsWith(quote)) {
      const base = upper.slice(0, -quote.length);
      if (known(base)) return CANON.get(base) ?? base;
    }
  }
  return upper;
}

/** Every name the underlying goes by on a market, canonical first (listing names, as the venues
 * write them: `kPEPE`). */
export function aliasesOf(sym: string): string[] {
  const canon = canonical(sym);
  return [...(NAMES.get(canon) ?? [canon])];
}

/** The asset class of an underlying, by its canonical name, else the name itself, else a
 * commodity's part of it (`HEATINGOIL`); null when unknown. */
export function classOf(sym: string): AssetClass | null {
  const canon = canonical(sym);
  const bare = undex(sym).bare.toUpperCase();
  const hit = CLASS.get(canon) ?? CLASS.get(bare);
  if (hit !== undefined) return hit;
  return commodityPart(bare) ? "commodities" : null;
}

function commodityPart(name: string): boolean {
  return /(?:^|[^A-Z0-9_])OIL(?:$|[^A-Z0-9_])/.test(name) || COMMODITY_PARTS.some((part) => name.includes(part));
}
